import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar, Users, BookOpen, Award, User, Download, Upload, Plus, Trash2, Eye, Edit, Check, X, Bell, Filter, FileText, CheckCircle, Clock, Search, ChevronDown, Menu, Info, RefreshCw, AlertTriangle, Mail, BarChart3, ClipboardList, Lock, LockOpen } from 'lucide-react';
import CompetitionAnalysis from '../CompetitionAnalysis';
import EventEnrollments from '../EventEnrollments';
import { AdminPushNotifications } from '../PushNotifications';
import { AdminMassEmail } from '../AdminMassEmail';
import { AdminAuditLog } from '../AdminAuditLog';
import { AdminAnalytics } from '../AdminAnalytics';
import StarRating from '../StarRating';
import AreaFiltro from '../AreaFiltro';
import MultiTeacherSelect from '../MultiTeacherSelect';
import { toast } from 'sonner';
import { useDashboard } from './DashboardContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const ctx = useDashboard();
  const {
    user, activeRole, users, events,
    teachers, formatori,
    activeAdminTab, setActiveAdminTab,
    adminMobileMenuOpen, setAdminMobileMenuOpen,
    enrollmentsCount, areaFiltroCount, usersCount,
    getFilteredAndSortedUsers, userFilter, setUserFilter, userSortOrder, setUserSortOrder,
    collapsedRoles, setCollapsedRoles,
    selectedUserId, setSelectedUserId,
    selectedEventEnrollments, setSelectedEventEnrollments,
    newUser, setNewUser, createUser,
    newEvent, setNewEvent, posterFile, setPosterFile, uploadingPoster, createEvent,
    editingEvent, setEditingEvent, editEventData, setEditEventData, editPosterFile, setEditPosterFile,
    pendingEnrollments, pendingPaymentEnrollments,
    matchAnalyses, detailedMatchAnalyses, allMatchAnalyses, allDetailedMatchAnalyses,
    newMatchAnalysis, setNewMatchAnalysis,
    newDetailedAnalysis, setNewDetailedAnalysis,
    analysisFilters, setAnalysisFilters, filterMatchAnalyses, clearAnalysisFilters,
    selectedAnalysis, setSelectedAnalysis,
    selectedDetailedAnalysis, setSelectedDetailedAnalysis,
    createMatchAnalysis, createDetailedMatchAnalysis,
    sendAnalysisEmail,
    allCertificates, newCertificate, setNewCertificate,
    certificateFilters, setCertificateFilters, filterCertificates, clearCertificateFilters,
    certificateFile, setCertificateFile, createCertificateWithFile,
    generatingCertificates, regeneratingCertificates,
    selectedMaestroFilter, setSelectedMaestroFilter,
    maestrosForFilter, athletesCreatedByMaestros,
    fetchAthletesCreatedByMaestros, exportMaestroAthletesExcel,
    downloadPaymentPdf, approveMaestroAthleteCreation,
    searchFilters, setSearchFilters, searchResults, searchUsers, isSearching, clearSearchFilters,
    inconsistentUsers, fixingInconsistent, selectedInconsistentUsers,
    selectAllInconsistentUsers, fixSelectedInconsistentUsers, fixAllInconsistentUsers,
    technicalCreditsModal, setTechnicalCreditsModal,
    formatDate, formatDateRange, fetchUsers,
    fetchStudyMaterials, setSelectedEventForMaterials,
    setOnlineMeetingLinks, setSelectedEventForLinks,
    fetchTeacherEventEnrollments, fetchFormatoreEventEnrollments,
    processEnrollmentDeletionRequest, enrollmentDeletionRequests,
    approveEnrollment, rejectEnrollment, approveUser, deleteUser,
    confirmPaymentReceived, approveEnrollmentUnpaid, deleteCertificate, deleteMatchAnalysis, deleteDetailedMatchAnalysis,
    deleteEvent, generateEventCertificates, regenerateCertificate, regenerateMissingCertificates, regenerateAllMissingCertificates,
    openPasswordResetModal, startEditingEvent, updateEvent, toggleEventEnrollment,
    toggleInconsistentUserSelection,
    incompleteProfiles, sendProfileReminder, sendingReminder, fetchIncompleteProfiles,
    sendPaymentReminder, sendingPaymentReminder,
    sendApprovalReminder, sendingApprovalReminder,
    matchResultForm, setMatchResultForm, adminMatchAnalyses, submitMatchResult,
    rejectedEnrollments, deleteRejectedEnrollment
  } = ctx;

  // Local state for event search in "Eventi Esistenti" list
  const [eventSearchQuery, setEventSearchQuery] = useState('');

  // Local state for "Risultati Inseriti" (Match Analysis) athlete search + expanded rows
  const [matchResultsSearch, setMatchResultsSearch] = useState('');
  const [expandedMatchResultId, setExpandedMatchResultId] = useState(null);

  // Local state for "Ranking" tab
  const [rankingData, setRankingData] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingSearch, setRankingSearch] = useState('');
  const [rankingExporting, setRankingExporting] = useState(null); // 'pdf' | 'excel' | null
  const [rankingAcademicYear, setRankingAcademicYear] = useState('all'); // 'all' | '2025/2026' | ...
  const [rankingAvailableYears, setRankingAvailableYears] = useState([]);

  const fetchRankingYears = React.useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/ranking/academic-years`);
      const years = Array.isArray(resp.data?.years) ? resp.data.years : [];
      setRankingAvailableYears(years);
    } catch {
      setRankingAvailableYears([]);
    }
  }, []);

  const fetchRanking = React.useCallback(async (yearOverride) => {
    setRankingLoading(true);
    try {
      const year = yearOverride !== undefined ? yearOverride : rankingAcademicYear;
      const params = year && year !== 'all' ? { academic_year: year } : {};
      const resp = await axios.get(`${API}/admin/ranking`, { params });
      setRankingData(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      toast.error('Errore nel caricamento del ranking');
      setRankingData([]);
    } finally {
      setRankingLoading(false);
    }
  }, [rankingAcademicYear]);

  React.useEffect(() => {
    if (activeAdminTab === 'ranking') {
      fetchRankingYears();
      if (rankingData.length === 0 && !rankingLoading) {
        fetchRanking();
      }
    }
  }, [activeAdminTab, rankingData.length, rankingLoading, fetchRanking, fetchRankingYears]);

  const handleRankingYearChange = (year) => {
    setRankingAcademicYear(year);
    fetchRanking(year);
  };

  const filteredRanking = useMemo(() => {
    const q = rankingSearch.trim().toLowerCase();
    if (!q) return rankingData;
    return rankingData.filter((r) => {
      const full = `${r.surname || ''} ${r.name || ''} ${r.email || ''}`.toLowerCase();
      return full.includes(q);
    });
  }, [rankingData, rankingSearch]);

  const exportRanking = async (format) => {
    setRankingExporting(format);
    try {
      const params = rankingAcademicYear && rankingAcademicYear !== 'all' ? { academic_year: rankingAcademicYear } : {};
      const resp = await axios.get(`${API}/admin/ranking/export/${format}`, { responseType: 'blob', params });
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      const yearSuffix = rankingAcademicYear && rankingAcademicYear !== 'all' ? `_${rankingAcademicYear.replace('/', '-')}` : '';
      a.download = `Ranking_Atleti_CIBS${yearSuffix}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success(`Ranking esportato in ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Errore nell'esportazione ${format.toUpperCase()}`);
    } finally {
      setRankingExporting(null);
    }
  };

  const sortedFilteredMatchAnalyses = useMemo(() => {
    const list = Array.isArray(adminMatchAnalyses) ? [...adminMatchAnalyses] : [];
    const q = matchResultsSearch.trim().toLowerCase();
    const filtered = q
      ? list.filter((a) => (a?.athlete_name || '').toLowerCase().includes(q))
      : list;
    // Sort newest-first by created_at
    filtered.sort((a, b) => {
      const ad = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bd = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bd - ad;
    });
    return filtered;
  }, [adminMatchAnalyses, matchResultsSearch]);

  // Sort: events with date_tbd first, then chronological by start_date asc.
  // Filter by title/location matching search query (case-insensitive).
  const sortedFilteredEvents = useMemo(() => {
    const list = Array.isArray(events) ? [...events] : [];
    const q = eventSearchQuery.trim().toLowerCase();
    const filtered = q
      ? list.filter((ev) => {
          const title = (ev?.title || '').toLowerCase();
          const location = (ev?.location || '').toLowerCase();
          return title.includes(q) || location.includes(q);
        })
      : list;
    filtered.sort((a, b) => {
      const aTbd = !!a?.date_tbd;
      const bTbd = !!b?.date_tbd;
      if (aTbd !== bTbd) return aTbd ? -1 : 1; // TBD events come first
      // Both same TBD status -> sort chronologically by start_date desc (newest first).
      // Missing dates go after dated ones.
      const aDate = a?.start_date ? new Date(a.start_date).getTime() : Number.NEGATIVE_INFINITY;
      const bDate = b?.start_date ? new Date(b.start_date).getTime() : Number.NEGATIVE_INFINITY;
      if (aDate !== bDate) return bDate - aDate;
      // Tie-breaker by title
      return (a?.title || '').localeCompare(b?.title || '', 'it');
    });
    return filtered;
  }, [events, eventSearchQuery]);

  return (
          <Tabs value={activeAdminTab} onValueChange={setActiveAdminTab} className="space-y-6" data-testid="admin-dashboard">
            {/* Desktop TabsList - hidden on mobile */}
            <TabsList className="hidden md:flex w-full flex-wrap gap-1">
              <TabsTrigger value="users" data-testid="users-tab">Utenti</TabsTrigger>
              <TabsTrigger value="search" data-testid="search-tab">Cerca</TabsTrigger>
              <TabsTrigger value="events" data-testid="admin-events-tab">Eventi</TabsTrigger>
              <TabsTrigger value="enrollments" data-testid="admin-enrollments-tab">
                Iscrizioni {enrollmentsCount.pending > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {enrollmentsCount.pending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="competition" data-testid="admin-competition-tab">Gara</TabsTrigger>
              <TabsTrigger value="area-filtro" data-testid="admin-area-filtro-tab">
                <Filter className="w-4 h-4 mr-1" />
                Area FILTRO {areaFiltroCount > 0 && (
                  <span className="ml-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {areaFiltroCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="analysis" data-testid="admin-analysis-tab">Analysis</TabsTrigger>
              <TabsTrigger value="detailed" data-testid="admin-detailed-tab">Dettagliate</TabsTrigger>
              <TabsTrigger value="match-results" data-testid="admin-match-results-tab">Risultati MA</TabsTrigger>
              <TabsTrigger value="ranking" data-testid="admin-ranking-tab">
                <Award className="w-4 h-4 mr-1" />
                Ranking
              </TabsTrigger>
              <TabsTrigger value="certificates" data-testid="admin-certificates-tab">Certificati</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="admin-notifications-tab">
                <Bell className="w-4 h-4 mr-1" />
                Notifiche
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="admin-email-tab">
                <Mail className="w-4 h-4 mr-1" />
                Email
              </TabsTrigger>
              <TabsTrigger value="maestro-athletes" data-testid="admin-maestro-athletes-tab">
                <Users className="w-4 h-4 mr-1" />
                Atleti Maestri
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="admin-analytics-tab">
                <BarChart3 className="w-4 h-4 mr-1" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="admin-audit-tab">
                <ClipboardList className="w-4 h-4 mr-1" />
                Audit Log
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="admin-reports-tab">Report</TabsTrigger>
              <TabsTrigger value="incomplete-profiles" data-testid="admin-incomplete-profiles-tab">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Profili Incompleti
                {incompleteProfiles.length > 0 && (
                  <Badge variant="destructive" className="ml-1 text-xs">{incompleteProfiles.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
            
            {/* Mobile Menu for Admin - visible only on mobile */}
            <div className="md:hidden relative">
              <button
                onClick={() => setAdminMobileMenuOpen(!adminMobileMenuOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm"
                data-testid="admin-mobile-menu-button"
              >
                <div className="flex items-center space-x-2">
                  <Menu className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-600">Scegli sezione</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-red-600">
                    {activeAdminTab === 'users' && 'Utenti'}
                    {activeAdminTab === 'search' && 'Cerca'}
                    {activeAdminTab === 'events' && 'Eventi'}
                    {activeAdminTab === 'enrollments' && `Iscrizioni${enrollmentsCount.pending > 0 ? ` (${enrollmentsCount.pending})` : ''}`}
                    {activeAdminTab === 'competition' && 'Gara'}
                    {activeAdminTab === 'area-filtro' && `Area FILTRO${areaFiltroCount > 0 ? ` (${areaFiltroCount})` : ''}`}
                    {activeAdminTab === 'analysis' && 'Analysis'}
                    {activeAdminTab === 'detailed' && 'Dettagliate'}
                    {activeAdminTab === 'ranking' && 'Ranking'}
                    {activeAdminTab === 'certificates' && 'Certificati'}
                    {activeAdminTab === 'notifications' && 'Notifiche'}
                    {activeAdminTab === 'email' && 'Email'}
                    {activeAdminTab === 'analytics' && 'Analytics'}
                    {activeAdminTab === 'audit' && 'Audit Log'}
                    {activeAdminTab === 'reports' && 'Report'}
                    {activeAdminTab === 'incomplete-profiles' && `Profili Incompleti${incompleteProfiles.length > 0 ? ` (${incompleteProfiles.length})` : ''}`}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${adminMobileMenuOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {adminMobileMenuOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {[
                    { value: 'users', label: 'Utenti' },
                    { value: 'search', label: 'Cerca' },
                    { value: 'events', label: 'Eventi' },
                    { value: 'enrollments', label: `Iscrizioni${enrollmentsCount.pending > 0 ? ` (${enrollmentsCount.pending})` : ''}` },
                    { value: 'competition', label: 'Gara' },
                    { value: 'area-filtro', label: `Area FILTRO${areaFiltroCount > 0 ? ` (${areaFiltroCount})` : ''}` },
                    { value: 'analysis', label: 'Analysis' },
                    { value: 'detailed', label: 'Dettagliate' },
                    { value: 'ranking', label: 'Ranking' },
                    { value: 'certificates', label: 'Certificati' },
                    { value: 'notifications', label: 'Notifiche' },
                    { value: 'email', label: 'Email' },
                    { value: 'maestro-athletes', label: 'Atleti Maestri' },
                    { value: 'analytics', label: 'Analytics' },
                    { value: 'audit', label: 'Audit Log' },
                    { value: 'reports', label: 'Report' },
                    { value: 'incomplete-profiles', label: `Profili Incompleti${incompleteProfiles.length > 0 ? ` (${incompleteProfiles.length})` : ''}` }
                  ].map((item) => (
                    <button
                      key={item.value}
                      onClick={() => {
                        setActiveAdminTab(item.value);
                        setAdminMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 ${
                        activeAdminTab === item.value ? 'bg-red-50 text-red-600 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Push Notifications Tab for Admin */}
            <TabsContent value="notifications" data-testid="admin-notifications-content">
              <AdminPushNotifications />
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email" data-testid="admin-email-content">
              <AdminMassEmail />
            </TabsContent>

            {/* Maestro Athletes Tab for Admin */}
            <TabsContent value="maestro-athletes" data-testid="admin-maestro-athletes-content">
              <Card>
                <CardHeader>
                  <CardTitle>Atleti Creati dai Maestri</CardTitle>
                  <CardDescription>Storico delle utenze create dai maestri (in attesa di approvazione o approvate)</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filter and Export Controls */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="maestro-filter" className="text-sm whitespace-nowrap">Filtra per Maestro:</Label>
                      <select
                        id="maestro-filter"
                        value={selectedMaestroFilter}
                        onChange={(e) => {
                          setSelectedMaestroFilter(e.target.value);
                          fetchAthletesCreatedByMaestros(e.target.value === 'all' ? null : e.target.value);
                        }}
                        className="border rounded-md px-3 py-1.5 text-sm min-w-[180px]"
                        data-testid="maestro-filter-select"
                      >
                        <option value="all">Tutti i Maestri</option>
                        {maestrosForFilter.map((m) => (
                          <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportMaestroAthletesExcel}
                      className="flex items-center space-x-2"
                      data-testid="export-maestro-athletes-excel"
                    >
                      <Download className="w-4 h-4" />
                      <span>Esporta Excel</span>
                    </Button>
                  </div>

                  {athletesCreatedByMaestros.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Nessun atleta creato dai maestri al momento.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3 font-semibold">Data</th>
                              <th className="text-left p-3 font-semibold">Maestro</th>
                              <th className="text-left p-3 font-semibold">Atleta</th>
                              <th className="text-left p-3 font-semibold">Email</th>
                              <th className="text-left p-3 font-semibold">Azione</th>
                              <th className="text-left p-3 font-semibold">Status</th>
                              <th className="text-left p-3 font-semibold">PDF</th>
                              <th className="text-left p-3 font-semibold">Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {athletesCreatedByMaestros.map((creation) => (
                              <tr key={creation.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  {new Date(creation.created_at).toLocaleDateString('it-IT', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="p-3">
                                  <span className="font-medium text-orange-600">{creation.maestro_name}</span>
                                </td>
                                <td className="p-3">
                                  <span className="font-medium">{creation.athlete_name}</span>
                                </td>
                                <td className="p-3 text-gray-500">{creation.athlete_email}</td>
                                <td className="p-3">
                                  {creation.action_type === 'creation' ? (
                                    <Badge className="bg-green-100 text-green-800">Nuova Creazione</Badge>
                                  ) : (
                                    <Badge className="bg-orange-100 text-orange-800">Password Sovrascritta</Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {creation.status === 'pending' ? (
                                    <Badge className="bg-yellow-100 text-yellow-800">
                                      <Clock className="w-3 h-3 mr-1" />
                                      In Attesa
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-green-100 text-green-800">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Approvato
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {creation.has_payment_pdf ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => downloadPaymentPdf(creation.id, creation.payment_pdf_filename)}
                                      className="text-blue-600 hover:text-blue-800 p-1"
                                      data-testid={`download-pdf-${creation.id}`}
                                    >
                                      <FileText className="w-4 h-4 mr-1" />
                                      Scarica
                                    </Button>
                                  ) : (
                                    <span className="text-gray-400 text-xs">N/A</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  {creation.status === 'pending' && (
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => approveMaestroAthleteCreation(creation.id)}
                                      className="bg-green-600 hover:bg-green-700"
                                      data-testid={`approve-athlete-${creation.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Approva
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Competition Analysis Tab for Admin */}
            <TabsContent value="competition" data-testid="admin-competition-analysis-content">
              <CompetitionAnalysis user={user} activeRole={activeRole} />
            </TabsContent>
            
            {/* Area FILTRO Tab for Admin */}
            <TabsContent value="area-filtro" data-testid="admin-area-filtro-content">
              <AreaFiltro />
            </TabsContent>
            
            <TabsContent value="users" data-testid="admin-users-content">
              <div className="space-y-6">
                {/* Users Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="w-5 h-5" />
                      <span>Statistiche Utenti</span>
                    </CardTitle>
                    <CardDescription>
                      Panoramica degli utenti attivi della piattaforma per tipologia
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4" data-testid="admin-user-stats">
                      <div className="bg-gray-100 p-4 rounded-lg text-center" data-testid="stat-total">
                        <h3 className="font-semibold text-gray-700 text-sm">Totale</h3>
                        <p className="text-3xl font-bold text-gray-900">{usersCount.total ?? 0}</p>
                      </div>
                      <div className="bg-purple-100 p-4 rounded-lg text-center" data-testid="stat-admin">
                        <h3 className="font-semibold text-purple-700 text-sm">Admin</h3>
                        <p className="text-3xl font-bold text-purple-900">{usersCount.admin ?? 0}</p>
                      </div>
                      <div className="bg-blue-100 p-4 rounded-lg text-center" data-testid="stat-teacher">
                        <h3 className="font-semibold text-blue-700 text-sm">Insegnanti</h3>
                        <p className="text-3xl font-bold text-blue-900">{usersCount.teacher ?? 0}</p>
                      </div>
                      <div className="bg-indigo-100 p-4 rounded-lg text-center" data-testid="stat-formatore">
                        <h3 className="font-semibold text-indigo-700 text-sm">Formatori</h3>
                        <p className="text-3xl font-bold text-indigo-900">{usersCount.formatore ?? 0}</p>
                      </div>
                      <div className="bg-rose-100 p-4 rounded-lg text-center" data-testid="stat-maestro">
                        <h3 className="font-semibold text-rose-700 text-sm">Maestri</h3>
                        <p className="text-3xl font-bold text-rose-900">{usersCount.maestro ?? 0}</p>
                      </div>
                      <div className="bg-teal-100 p-4 rounded-lg text-center" data-testid="stat-contabile">
                        <h3 className="font-semibold text-teal-700 text-sm">Contabili</h3>
                        <p className="text-3xl font-bold text-teal-900">{usersCount.contabile ?? 0}</p>
                      </div>
                      <div className="bg-green-100 p-4 rounded-lg text-center" data-testid="stat-atleta">
                        <h3 className="font-semibold text-green-700 text-sm">Atleti</h3>
                        <p className="text-3xl font-bold text-green-900">{usersCount.atleta ?? 0}</p>
                      </div>
                      <div className="bg-yellow-100 p-4 rounded-lg text-center" data-testid="stat-pending">
                        <h3 className="font-semibold text-yellow-700 text-sm">In Attesa</h3>
                        <p className="text-3xl font-bold text-yellow-900">{usersCount.pending_approval ?? 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Inconsistent Users Alert */}
                {inconsistentUsers.length > 0 && (
                  <Card className="border-red-300 bg-red-50">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        <span>Utenti con Status Inconsistente ({inconsistentUsers.length})</span>
                      </CardTitle>
                      <CardDescription className="text-red-600">
                        Questi utenti hanno sia un profilo attivo che lo status PENDING. Seleziona e correggi.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllInconsistentUsers}
                          data-testid="select-all-inconsistent"
                        >
                          {selectedInconsistentUsers.length === inconsistentUsers.length ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={fixSelectedInconsistentUsers}
                          disabled={fixingInconsistent || selectedInconsistentUsers.length === 0}
                          className="bg-orange-600 hover:bg-orange-700"
                          data-testid="fix-selected-inconsistent"
                        >
                          {fixingInconsistent ? 'Correzione...' : `Correggi Selezionati (${selectedInconsistentUsers.length})`}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={fixAllInconsistentUsers}
                          disabled={fixingInconsistent}
                          data-testid="fix-all-inconsistent"
                        >
                          {fixingInconsistent ? 'Correzione...' : 'Correggi Tutti'}
                        </Button>
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-red-100 sticky top-0">
                            <tr>
                              <th className="p-2 text-left w-10">
                                <input
                                  type="checkbox"
                                  checked={selectedInconsistentUsers.length === inconsistentUsers.length && inconsistentUsers.length > 0}
                                  onChange={selectAllInconsistentUsers}
                                  className="rounded"
                                />
                              </th>
                              <th className="p-2 text-left">Nome</th>
                              <th className="p-2 text-left">Email</th>
                              <th className="p-2 text-left">Ruoli Attuali</th>
                              <th className="p-2 text-left">Approvato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inconsistentUsers.map((user) => (
                              <tr key={user.id} className="border-t hover:bg-red-50">
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedInconsistentUsers.includes(user.id)}
                                    onChange={() => toggleInconsistentUserSelection(user.id)}
                                    className="rounded"
                                  />
                                </td>
                                <td className="p-2 font-medium">{user.name} {user.surname}</td>
                                <td className="p-2 text-gray-600">{user.email}</td>
                                <td className="p-2">
                                  <div className="flex flex-wrap gap-1">
                                    {user.roles.map((role) => (
                                      <Badge 
                                        key={role} 
                                        className={role === 'pending' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}
                                      >
                                        {role}
                                      </Badge>
                                    ))}
                                  </div>
                                </td>
                                <td className="p-2">
                                  {user.approved ? (
                                    <Badge className="bg-green-100 text-green-800">Sì</Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-800">No</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <p className="text-xs text-red-600 mt-2">
                        La correzione rimuoverà "pending" dai ruoli e imposterà lo status su "Approvato"
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Create User Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Plus className="w-5 h-5" />
                      <span>Crea Nuovo Utente</span>
                    </CardTitle>
                    <CardDescription>
                      Aggiungi manualmente un nuovo utente alla piattaforma
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createUser} className="space-y-4" data-testid="create-user-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nome *</Label>
                          <Input
                            id="name"
                            value={newUser.name}
                            onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                            placeholder="Nome"
                            required
                            data-testid="new-user-name-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="surname">Cognome *</Label>
                          <Input
                            id="surname"
                            value={newUser.surname}
                            onChange={(e) => setNewUser({...newUser, surname: e.target.value})}
                            placeholder="Cognome"
                            required
                            data-testid="new-user-surname-input"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                          placeholder="email@esempio.com"
                          required
                          data-testid="new-user-email-input"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="password">Password *</Label>
                          <Input
                            id="password"
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                            placeholder="Password (min 6 caratteri)"
                            minLength={6}
                            required
                            data-testid="new-user-password-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="role">Ruolo *</Label>
                          <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value, roles: value === 'teacher-formatore' ? ['teacher', 'formatore'] : [value]})} data-testid="new-user-role-select">
                            <SelectTrigger>
                              <SelectValue placeholder="Seleziona ruolo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="atleta" data-testid="new-user-role-atleta">Atleta</SelectItem>
                              <SelectItem value="teacher" data-testid="new-user-role-teacher">Teacher</SelectItem>
                              <SelectItem value="formatore" data-testid="new-user-role-formatore">Formatore</SelectItem>
                              <SelectItem value="maestro" data-testid="new-user-role-maestro">Maestro</SelectItem>
                              <SelectItem value="contabile" data-testid="new-user-role-contabile">Contabile</SelectItem>
                              <SelectItem value="teacher-formatore" data-testid="new-user-role-teacher-formatore">Teacher + Formatore</SelectItem>
                              <SelectItem value="admin" data-testid="new-user-role-admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <Button type="submit" className="w-full" data-testid="create-user-button">
                        Crea Utente
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Users List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="w-5 h-5" />
                      <span>Utenti Registrati</span>
                    </CardTitle>
                    <CardDescription>
                      Approva e gestisci gli utenti registrati
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Filter and Sort Controls */}
                    <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Label className="text-sm font-medium whitespace-nowrap">Filtra per:</Label>
                        <Select value={userFilter} onValueChange={setUserFilter}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Tutti" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutti</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="teacher">Insegnanti</SelectItem>
                            <SelectItem value="formatore">Formatori</SelectItem>
                            <SelectItem value="maestro">Maestri</SelectItem>
                            <SelectItem value="contabile">Contabili</SelectItem>
                            <SelectItem value="atleta">Atleti</SelectItem>
                            <SelectItem value="pending">In Attesa</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Label className="text-sm font-medium whitespace-nowrap">Ordina per:</Label>
                        <Select value={userSortOrder} onValueChange={setUserSortOrder}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Ruolo (default)</SelectItem>
                            <SelectItem value="alphabetical">Alfabetico</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(userFilter !== 'all' || userSortOrder !== 'default') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setUserFilter('all');
                            setUserSortOrder('default');
                          }}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Rimuovi Filtri
                        </Button>
                      )}
                      
                      <div className="ml-auto">
                        <Badge variant="secondary">
                          {getFilteredAndSortedUsers().length} utenti
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-4" data-testid="users-list">
                      {/* PENDING USERS - Always visible in open list */}
                      {(() => {
                        const pendingUsers = getFilteredAndSortedUsers().filter(u => !u.approved);
                        if (pendingUsers.length > 0) {
                          return (
                            <div className="mb-6">
                              <div className="flex items-center space-x-2 mb-4 pb-2 border-b-2 border-yellow-400">
                                <Clock className="w-5 h-5 text-yellow-600" />
                                <h3 className="font-bold text-lg text-yellow-700">
                                  Utenti in Attesa di Approvazione ({pendingUsers.length})
                                </h3>
                              </div>
                              <div className="space-y-3">
                                {pendingUsers.map((u) => (
                                  <div key={u.id} className="flex items-center justify-between p-4 border-2 border-yellow-200 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors" data-testid={`user-${u.id}`}>
                                    <div className="flex items-center space-x-3">
                                      <div className="w-10 h-10 bg-yellow-200 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-yellow-700" />
                                      </div>
                                      <div>
                                        <h3 className="font-medium" data-testid={`user-name-${u.id}`}>{u.name} {u.surname}</h3>
                                        <p className="text-sm text-gray-500" data-testid={`user-email-${u.id}`}>{u.email}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                          <Badge variant="secondary" className="bg-yellow-500 text-white" data-testid={`user-pending-${u.id}`}>
                                            <Clock className="w-3 h-3 mr-1" />
                                            In attesa
                                          </Badge>
                                          {(u.roles?.length > 0 || u.role) && (
                                            <Badge variant="outline" className="border-orange-400 text-orange-600" data-testid={`user-requested-role-${u.id}`}>
                                              Richiesto: {
                                                (u.roles?.[0] || u.role) === 'atleta' ? 'Atleta' :
                                                (u.roles?.[0] || u.role) === 'teacher' ? 'Insegnante' :
                                                (u.roles?.[0] || u.role) === 'formatore' ? 'Formatore' :
                                                (u.roles?.[0] || u.role) === 'maestro' ? 'Maestro' :
                                                (u.roles?.[0] || u.role)?.toUpperCase()
                                              }
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        onClick={() => sendApprovalReminder(u.id)}
                                        size="sm"
                                        variant="outline"
                                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                        disabled={sendingApprovalReminder[u.id]}
                                        data-testid={`send-approval-reminder-${u.id}`}
                                      >
                                        <Mail className="w-4 h-4 mr-1" />
                                        {sendingApprovalReminder[u.id] ? 'Invio...' : 'Sollecito'}
                                      </Button>
                                      <Button
                                        onClick={() => setSelectedUserId(u.id)}
                                        size="sm"
                                        variant="outline"
                                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                        data-testid={`view-profile-${u.id}`}
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        Profilo
                                      </Button>
                                      <Button
                                        onClick={() => approveUser(u.id)}
                                        size="sm"
                                        className="bg-green-500 hover:bg-green-600 text-white"
                                        data-testid={`approve-user-${u.id}`}
                                      >
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        Approva
                                      </Button>
                                      <Button
                                        onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)}
                                        size="sm"
                                        variant="destructive"
                                        data-testid={`delete-user-${u.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* APPROVED USERS - Grouped by role in collapsible sections */}
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2 mb-2 pb-2 border-b">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h3 className="font-bold text-lg text-gray-700">Utenti Attivi (per ruolo)</h3>
                        </div>

                        {/* Admin Users */}
                        {(() => {
                          const adminUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('admin') || u.role === 'admin'));
                          if (adminUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, admin: !prev.admin}))}
                                className="w-full flex items-center justify-between p-4 bg-purple-100 hover:bg-purple-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-purple-800">Admin ({adminUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-purple-600 transition-transform ${!collapsedRoles.admin ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.admin && (
                                <div className="divide-y">
                                  {adminUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-purple-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Teacher Users */}
                        {(() => {
                          const teacherUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('teacher') || u.role === 'teacher') && !u.roles?.includes('admin') && u.role !== 'admin');
                          if (teacherUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, teacher: !prev.teacher}))}
                                className="w-full flex items-center justify-between p-4 bg-blue-100 hover:bg-blue-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-blue-800">Insegnanti ({teacherUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-blue-600 transition-transform ${!collapsedRoles.teacher ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.teacher && (
                                <div className="divide-y">
                                  {teacherUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                        <Button onClick={() => openPasswordResetModal(u.id, u.email)} size="sm" variant="outline" className="text-xs text-orange-600">
                                          <Edit className="w-3 h-3 mr-1" />Reset
                                        </Button>
                                        <Button onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)} size="sm" variant="destructive" className="text-xs">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Formatore Users */}
                        {(() => {
                          const formatoreUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('formatore') || u.role === 'formatore') && !u.roles?.includes('admin') && !u.roles?.includes('teacher') && u.role !== 'admin' && u.role !== 'teacher');
                          if (formatoreUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, formatore: !prev.formatore}))}
                                className="w-full flex items-center justify-between p-4 bg-indigo-100 hover:bg-indigo-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-indigo-800">Formatori ({formatoreUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-indigo-600 transition-transform ${!collapsedRoles.formatore ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.formatore && (
                                <div className="divide-y">
                                  {formatoreUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-indigo-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                        <Button onClick={() => openPasswordResetModal(u.id, u.email)} size="sm" variant="outline" className="text-xs text-orange-600">
                                          <Edit className="w-3 h-3 mr-1" />Reset
                                        </Button>
                                        <Button onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)} size="sm" variant="destructive" className="text-xs">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Maestro Users */}
                        {(() => {
                          const maestroUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('maestro') || u.role === 'maestro') && !u.roles?.includes('admin') && !u.roles?.includes('teacher') && !u.roles?.includes('formatore') && u.role !== 'admin' && u.role !== 'teacher' && u.role !== 'formatore');
                          if (maestroUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, maestro: !prev.maestro}))}
                                className="w-full flex items-center justify-between p-4 bg-orange-100 hover:bg-orange-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-orange-800">Maestri ({maestroUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-orange-600 transition-transform ${!collapsedRoles.maestro ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.maestro && (
                                <div className="divide-y">
                                  {maestroUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-orange-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                        <Button onClick={() => openPasswordResetModal(u.id, u.email)} size="sm" variant="outline" className="text-xs text-orange-600">
                                          <Edit className="w-3 h-3 mr-1" />Reset
                                        </Button>
                                        <Button onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)} size="sm" variant="destructive" className="text-xs">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Contabile Users */}
                        {(() => {
                          const contabileUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('contabile') || u.role === 'contabile') && !u.roles?.includes('admin') && u.role !== 'admin');
                          if (contabileUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, contabile: !prev.contabile}))}
                                className="w-full flex items-center justify-between p-4 bg-teal-100 hover:bg-teal-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-teal-800">Contabili ({contabileUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-teal-600 transition-transform ${!collapsedRoles.contabile ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.contabile && (
                                <div className="divide-y">
                                  {contabileUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-teal-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                        <Button onClick={() => openPasswordResetModal(u.id, u.email)} size="sm" variant="outline" className="text-xs text-teal-600">
                                          <Edit className="w-3 h-3 mr-1" />Reset
                                        </Button>
                                        <Button onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)} size="sm" variant="destructive" className="text-xs">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Atleta Users */}
                        {(() => {
                          const atletaUsers = getFilteredAndSortedUsers().filter(u => u.approved && (u.roles?.includes('atleta') || u.role === 'atleta') && !u.roles?.includes('admin') && !u.roles?.includes('teacher') && !u.roles?.includes('formatore') && !u.roles?.includes('maestro') && u.role !== 'admin' && u.role !== 'teacher' && u.role !== 'formatore' && u.role !== 'maestro');
                          if (atletaUsers.length === 0) return null;
                          return (
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => setCollapsedRoles(prev => ({...prev, atleta: !prev.atleta}))}
                                className="w-full flex items-center justify-between p-4 bg-green-100 hover:bg-green-200 transition-colors"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                    <User className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="font-semibold text-green-800">Atleti ({atletaUsers.length})</span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-green-600 transition-transform ${!collapsedRoles.atleta ? 'rotate-180' : ''}`} />
                              </button>
                              {!collapsedRoles.atleta && (
                                <div className="divide-y">
                                  {atletaUsers.map((u) => (
                                    <div key={u.id} className="flex items-center justify-between p-3 hover:bg-gray-50" data-testid={`user-${u.id}`}>
                                      <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                          <User className="w-4 h-4 text-green-600" />
                                        </div>
                                        <div>
                                          <h4 className="font-medium text-sm">{u.name} {u.surname}</h4>
                                          <p className="text-xs text-gray-500">{u.email}</p>
                                        </div>
                                      </div>
                                      <div className="flex space-x-2">
                                        <Button onClick={() => setSelectedUserId(u.id)} size="sm" variant="outline" className="text-xs">
                                          <Eye className="w-3 h-3 mr-1" />Profilo
                                        </Button>
                                        <Button
                                          onClick={() => setTechnicalCreditsModal({
                                            isOpen: true,
                                            athleteId: u.id,
                                            athleteName: `${u.name} ${u.surname}`,
                                            credits: '',
                                            academicYear: '2025/2026',
                                            notes: ''
                                          })}
                                          size="sm"
                                          variant="outline"
                                          className="text-xs text-green-600"
                                        >
                                          <Award className="w-3 h-3 mr-1" />Crediti
                                        </Button>
                                        <Button onClick={() => openPasswordResetModal(u.id, u.email)} size="sm" variant="outline" className="text-xs text-orange-600">
                                          <Edit className="w-3 h-3 mr-1" />Reset
                                        </Button>
                                        <Button onClick={() => deleteUser(u.id, `${u.name} ${u.surname}`)} size="sm" variant="destructive" className="text-xs">
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            {/* Search Tab */}
            <TabsContent value="search" data-testid="admin-search-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Cerca Utenti</span>
                  </CardTitle>
                  <CardDescription>
                    Ricerca utenti per Nome, Cognome, Email, Codice Fiscale o ASA di Riferimento
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Search Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="search_name">Nome</Label>
                        <input
                          id="search_name"
                          type="text"
                          value={searchFilters.name}
                          onChange={(e) => setSearchFilters({...searchFilters, name: e.target.value})}
                          placeholder="Inserisci nome"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="search_surname">Cognome</Label>
                        <input
                          id="search_surname"
                          type="text"
                          value={searchFilters.surname}
                          onChange={(e) => setSearchFilters({...searchFilters, surname: e.target.value})}
                          placeholder="Inserisci cognome"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="search_email">Email</Label>
                        <input
                          id="search_email"
                          type="email"
                          value={searchFilters.email || ''}
                          onChange={(e) => setSearchFilters({...searchFilters, email: e.target.value.toLowerCase()})}
                          placeholder="Inserisci email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="search_fiscal_code">Codice Fiscale</Label>
                        <input
                          id="search_fiscal_code"
                          type="text"
                          value={searchFilters.fiscal_code}
                          onChange={(e) => setSearchFilters({...searchFilters, fiscal_code: e.target.value.toUpperCase()})}
                          placeholder="Inserisci codice fiscale"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          maxLength={16}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="search_asa">ASA di Riferimento</Label>
                        <input
                          id="search_asa"
                          type="text"
                          value={searchFilters.asa_reference}
                          onChange={(e) => setSearchFilters({...searchFilters, asa_reference: e.target.value})}
                          placeholder="Inserisci ASA"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="search_maestro">Maestro</Label>
                        <select
                          id="search_maestro"
                          value={searchFilters.maestro_id || ''}
                          onChange={(e) => setSearchFilters({...searchFilters, maestro_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          data-testid="search-maestro-select"
                        >
                          <option value="">Tutti i Maestri</option>
                          {users.filter(u => (u.roles || []).includes('maestro') && u.approved).sort((a, b) => (a.surname || '').localeCompare(b.surname || '')).map(m => (
                            <option key={m.id} value={m.id}>{m.surname} {m.name}{m.school_name ? ` - ${m.school_name}` : ''}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex space-x-3">
                      <Button 
                        onClick={searchUsers} 
                        disabled={isSearching}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {isSearching ? 'Ricerca in corso...' : 'Cerca'}
                      </Button>
                      <Button 
                        onClick={clearSearchFilters}
                        variant="outline"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Pulisci Filtri
                      </Button>
                    </div>
                    
                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4">
                          Risultati della ricerca ({searchResults.length})
                        </h3>
                        <div className="space-y-3">
                          {searchResults.map((user) => (
                            <div 
                              key={user.id} 
                              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h4 className="font-semibold text-lg">
                                      {user.name} {user.surname}
                                    </h4>
                                    {user.roles && user.roles.map((role) => (
                                      <Badge key={role} className={
                                        role === 'admin' ? 'bg-red-500' :
                                        role === 'teacher' ? 'bg-green-500' :
                                        role === 'formatore' ? 'bg-purple-500' :
                                        role === 'atleta' ? 'bg-blue-500' : 'bg-gray-500'
                                      }>
                                        {role.toUpperCase()}
                                      </Badge>
                                    ))}
                                    {user.approved ? (
                                      <Badge className="bg-green-500">Approvato</Badge>
                                    ) : (
                                      <Badge className="bg-yellow-500">In Attesa</Badge>
                                    )}
                                  </div>
                                  <div className="space-y-1 text-sm text-gray-600">
                                    <p><strong>Email:</strong> {user.email}</p>
                                    {user.athlete_number && (
                                      <p><strong>Numero Atleta:</strong> {user.athlete_number}</p>
                                    )}
                                    {user.fiscal_code && (
                                      <p><strong>Codice Fiscale:</strong> {user.fiscal_code}</p>
                                    )}
                                    {user.birth_date && (
                                      <p><strong>Data di Nascita:</strong> {user.birth_date}</p>
                                    )}
                                    {user.athlete_class && (
                                      <p><strong>Classe:</strong> {user.athlete_class}</p>
                                    )}
                                    {user.asa_reference && (
                                      <p><strong>ASA di Riferimento:</strong> {user.asa_reference}</p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedUserId(user.id)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Visualizza
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="events" data-testid="admin-events-content">
              <div className="space-y-6">
                {/* Create Event Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Plus className="w-5 h-5" />
                      <span>Crea Nuovo Evento</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={createEvent} className="space-y-4" data-testid="create-event-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Titolo *</Label>
                          <Input
                            id="title"
                            value={newEvent.title}
                            onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                            placeholder="Titolo dell'evento"
                            required
                            data-testid="event-title-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="type">Tipo *</Label>
                          <Select value={newEvent.type} onValueChange={(value) => setNewEvent({...newEvent, type: value})} data-testid="event-type-select">
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="stage" data-testid="type-stage">Stage</SelectItem>
                              <SelectItem value="lecture" data-testid="type-lecture">Lecture</SelectItem>
                              <SelectItem value="online" data-testid="type-online">Online</SelectItem>
                              <SelectItem value="formazione_insegnanti" data-testid="type-formazione">Formazione Insegnanti</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="description">Descrizione *</Label>
                        <Textarea
                          id="description"
                          value={newEvent.description}
                          onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                          placeholder="Descrizione dell'evento"
                          required
                          data-testid="event-description-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={newEvent.location}
                          onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                          placeholder="Indirizzo o sede dell'evento"
                          data-testid="event-location-input"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="start_date">Data Inizio {!newEvent.date_tbd && '*'}</Label>
                          <Input
                            id="start_date"
                            type="date"
                            value={newEvent.start_date}
                            onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                            required={!newEvent.date_tbd}
                            disabled={newEvent.date_tbd}
                            className={newEvent.date_tbd ? 'opacity-40' : ''}
                            data-testid="event-start-date-input"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="end_date">Data Fine {!newEvent.date_tbd && '*'}</Label>
                          <Input
                            id="end_date"
                            type="date"
                            value={newEvent.end_date}
                            onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                            required={!newEvent.date_tbd}
                            disabled={newEvent.date_tbd}
                            className={newEvent.date_tbd ? 'opacity-40' : ''}
                            min={newEvent.start_date}
                            data-testid="event-end-date-input"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="date_tbd"
                          checked={newEvent.date_tbd}
                          onChange={(e) => setNewEvent({...newEvent, date_tbd: e.target.checked, start_date: e.target.checked ? '' : newEvent.start_date, end_date: e.target.checked ? '' : newEvent.end_date})}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid="event-date-tbd-checkbox"
                        />
                        <Label htmlFor="date_tbd" className="text-sm font-medium cursor-pointer">
                          DATA DA DEFINIRE
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="dance_analysis"
                          checked={newEvent.dance_analysis}
                          onChange={(e) => setNewEvent({...newEvent, dance_analysis: e.target.checked})}
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          data-testid="event-dance-analysis-checkbox"
                        />
                        <Label htmlFor="dance_analysis" className="text-sm font-medium cursor-pointer">
                          DANCE ANALYSIS
                        </Label>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor="teacher_ids">
                            {newEvent.type === 'formazione_insegnanti' ? 'Formatori' : 'Insegnanti/Relatori'}
                          </Label>
                          <MultiTeacherSelect
                            teachers={newEvent.type === 'formazione_insegnanti' ? formatori : teachers}
                            selectedTeacherIds={newEvent.teacher_ids}
                            onChange={(ids) => setNewEvent({...newEvent, teacher_ids: ids})}
                            dataTestId="event-teachers-select"
                          />
                        </div>
                      </div>

                      {/* Altri Insegnanti/Relatori Field */}
                      <div className="space-y-2">
                        <Label htmlFor="external_teachers_text">Altri Insegnanti/Relatori</Label>
                        <Textarea
                          id="external_teachers_text"
                          value={newEvent.external_teachers_text}
                          onChange={(e) => setNewEvent({...newEvent, external_teachers_text: e.target.value})}
                          placeholder="Inserisci nomi di altri insegnanti o relatori non registrati (es: Mario Rossi, Giuseppe Verdi, ecc.)"
                          rows={3}
                          data-testid="external-teachers-text"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        
                        <div className="space-y-2">
                          <Label htmlFor="max_participants">Max Partecipanti</Label>
                          <Input
                            id="max_participants"
                            type="number"
                            min="1"
                            value={newEvent.max_participants}
                            onChange={(e) => setNewEvent({...newEvent, max_participants: parseInt(e.target.value) || 20})}
                            data-testid="event-max-participants-input"
                          />
                        </div>
                      </div>
                      
                      {/* Upload Locandina */}
                      <div className="space-y-2">
                        <Label htmlFor="poster">Locandina Evento (PNG, JPEG o PDF)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="poster"
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf"
                            onChange={(e) => setPosterFile(e.target.files[0])}
                            className="flex-1"
                          />
                          {posterFile && (
                            <Badge variant="outline" className="bg-green-50">
                              {posterFile.name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          Carica un'immagine PNG/JPEG o PDF che sarà visibile come locandina dell'evento
                        </p>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full" 
                        data-testid="create-event-button"
                        disabled={uploadingPoster}
                      >
                        {uploadingPoster ? 'Caricamento locandina...' : 'Crea Evento'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                
                {/* Events List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5" />
                      <span>Eventi Esistenti</span>
                    </CardTitle>
                    <CardDescription>
                      Eventi con &quot;Data da Definire&quot; in alto, poi dal più recente al più vecchio.
                    </CardDescription>
                    <div className="relative mt-3">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        value={eventSearchQuery}
                        onChange={(e) => setEventSearchQuery(e.target.value)}
                        placeholder="Cerca evento per titolo o location..."
                        className="pl-9"
                        data-testid="admin-events-search-input"
                      />
                      {eventSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setEventSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                          data-testid="admin-events-search-clear"
                          aria-label="Pulisci ricerca"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {eventSearchQuery && (
                      <p className="text-xs text-gray-500 mt-1" data-testid="admin-events-search-count">
                        {sortedFilteredEvents.length} risultat{sortedFilteredEvents.length === 1 ? 'o' : 'i'} su {events.length}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" data-testid="admin-events-list">
                      {sortedFilteredEvents.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-6" data-testid="admin-events-no-results">
                          {eventSearchQuery ? 'Nessun evento corrisponde alla ricerca' : 'Nessun evento'}
                        </p>
                      ) : sortedFilteredEvents.map((event) => (
                        <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`admin-event-${event.id}`}>
                          {editingEvent === event.id ? (
                            <div className="space-y-4" data-testid={`edit-event-${event.id}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                  value={editEventData.title}
                                  onChange={(e) => setEditEventData({...editEventData, title: e.target.value})}
                                  placeholder="Titolo evento"
                                  data-testid={`edit-event-title-${event.id}`}
                                />
                                <Select value={editEventData.type} onValueChange={(value) => setEditEventData({...editEventData, type: value})}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="stage">Stage</SelectItem>
                                    <SelectItem value="lecture">Lecture</SelectItem>
                                    <SelectItem value="online">Online</SelectItem>
                                    <SelectItem value="formazione_insegnanti">Formazione Insegnanti</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Textarea
                                value={editEventData.description}
                                onChange={(e) => setEditEventData({...editEventData, description: e.target.value})}
                                placeholder="Descrizione evento"
                                data-testid={`edit-event-description-${event.id}`}
                              />
                              <Input
                                value={editEventData.location || ''}
                                onChange={(e) => setEditEventData({...editEventData, location: e.target.value})}
                                placeholder="Location evento"
                                data-testid={`edit-event-location-${event.id}`}
                              />
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`edit-start-date-${event.id}`}>Data Inizio {!editEventData.date_tbd && '*'}</Label>
                                  <Input
                                    id={`edit-start-date-${event.id}`}
                                    type="date"
                                    value={editEventData.start_date || ''}
                                    onChange={(e) => setEditEventData({...editEventData, start_date: e.target.value})}
                                    disabled={editEventData.date_tbd}
                                    className={editEventData.date_tbd ? 'opacity-40' : ''}
                                    data-testid={`edit-event-start-date-${event.id}`}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`edit-end-date-${event.id}`}>Data Fine {!editEventData.date_tbd && '*'}</Label>
                                  <Input
                                    id={`edit-end-date-${event.id}`}
                                    type="date"
                                    value={editEventData.end_date || ''}
                                    onChange={(e) => setEditEventData({...editEventData, end_date: e.target.value})}
                                    min={editEventData.start_date}
                                    disabled={editEventData.date_tbd}
                                    className={editEventData.date_tbd ? 'opacity-40' : ''}
                                    data-testid={`edit-event-end-date-${event.id}`}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`edit-date-tbd-${event.id}`}
                                  checked={editEventData.date_tbd || false}
                                  onChange={(e) => setEditEventData({...editEventData, date_tbd: e.target.checked, start_date: e.target.checked ? '' : editEventData.start_date, end_date: e.target.checked ? '' : editEventData.end_date})}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  data-testid={`edit-event-date-tbd-${event.id}`}
                                />
                                <Label htmlFor={`edit-date-tbd-${event.id}`} className="text-sm font-medium cursor-pointer">
                                  DATA DA DEFINIRE
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`edit-dance-analysis-${event.id}`}
                                  checked={editEventData.dance_analysis || false}
                                  onChange={(e) => setEditEventData({...editEventData, dance_analysis: e.target.checked})}
                                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  data-testid={`edit-event-dance-analysis-${event.id}`}
                                />
                                <Label htmlFor={`edit-dance-analysis-${event.id}`} className="text-sm font-medium cursor-pointer">
                                  DANCE ANALYSIS
                                </Label>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                  type="number"
                                  min="1"
                                  value={editEventData.max_participants}
                                  onChange={(e) => setEditEventData({...editEventData, max_participants: parseInt(e.target.value) || 20})}
                                  placeholder="Max partecipanti"
                                  data-testid={`edit-event-max-participants-${event.id}`}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>
                                  {editEventData.type === 'formazione_insegnanti' ? 'Formatori' : 'Insegnanti/Relatori'}
                                </Label>
                                <MultiTeacherSelect
                                  teachers={editEventData.type === 'formazione_insegnanti' ? formatori : teachers}
                                  selectedTeacherIds={editEventData.teacher_ids || []}
                                  onChange={(ids) => setEditEventData({...editEventData, teacher_ids: ids})}
                                  dataTestId={`edit-event-teachers-${event.id}`}
                                />
                              </div>

                              {/* Altri Insegnanti/Relatori Field in Edit Mode */}
                              <div>
                                <Label htmlFor={`edit-external-teachers-text-${event.id}`}>Altri Insegnanti/Relatori</Label>
                                <Textarea
                                  id={`edit-external-teachers-text-${event.id}`}
                                  value={editEventData.external_teachers_text || ''}
                                  onChange={(e) => setEditEventData({...editEventData, external_teachers_text: e.target.value})}
                                  placeholder="Inserisci nomi di altri insegnanti o relatori non registrati"
                                  rows={3}
                                  data-testid={`edit-external-teachers-text-${event.id}`}
                                />
                              </div>

                              {/* Modifica Locandina */}
                              <div className="space-y-2">
                                <Label htmlFor={`edit-poster-${event.id}`}>Modifica Locandina (PNG, JPEG o PDF)</Label>
                                <div className="flex items-center space-x-4">
                                  <Input
                                    id={`edit-poster-${event.id}`}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    onChange={(e) => setEditPosterFile(e.target.files[0])}
                                    className="flex-1"
                                    data-testid={`edit-poster-input-${event.id}`}
                                  />
                                  {editPosterFile && (
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-green-600">
                                        Nuovo: {editPosterFile.name}
                                      </span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditPosterFile(null)}
                                        className="text-red-500 hover:text-red-700 p-1"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                {editEventData.poster_url && !editPosterFile && (
                                  <div className="flex items-center space-x-2 mt-2">
                                    <span className="text-sm text-gray-500">Locandina attuale:</span>
                                    <a
                                      href={`${BACKEND_URL}${editEventData.poster_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                                    >
                                      Visualizza
                                    </a>
                                  </div>
                                )}
                              </div>

                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => updateEvent(event.id)}
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  data-testid={`save-event-${event.id}`}
                                >
                                  Salva
                                </Button>
                                <Button
                                  onClick={() => setEditingEvent(null)}
                                  size="sm"
                                  variant="outline"
                                  data-testid={`cancel-edit-event-${event.id}`}
                                >
                                  Annulla
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center space-x-2 mb-1">
                                  {event.event_code && (
                                    <Badge variant="outline" className="font-mono text-xs" data-testid={`admin-event-code-${event.id}`}>
                                      {event.event_code}
                                    </Badge>
                                  )}
                                  <h3 className="font-semibold text-lg" data-testid={`admin-event-title-${event.id}`}>{event.title}</h3>
                                </div>
                                <p className="text-gray-600 mb-2" data-testid={`admin-event-description-${event.id}`}>{event.description}</p>
                                <div className="flex items-center flex-wrap gap-2 text-sm text-gray-500 mb-2">
                                  <span data-testid={`admin-event-date-${event.id}`}>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                                  <Badge variant="secondary" data-testid={`admin-event-type-${event.id}`}>{event.type}</Badge>
                                  <span data-testid={`admin-event-participants-${event.id}`}>{event.enrolled_count}/{event.max_participants} iscritti</span>
                                  {event.enrollment_closed && (
                                    <Badge variant="destructive" className="flex items-center gap-1" data-testid={`enrollment-closed-badge-${event.id}`}>
                                      <Lock className="w-3 h-3" />
                                      Iscrizioni Chiuse
                                    </Badge>
                                  )}
                                  {event.dance_analysis && (
                                    <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1" data-testid={`dance-analysis-badge-${event.id}`}>
                                      DANCE ANALYSIS
                                    </Badge>
                                  )}
                                </div>
                                {event.location && (
                                  <div className="text-sm text-gray-600 mb-2" data-testid={`admin-event-location-${event.id}`}>
                                    📍 {event.location}
                                  </div>
                                )}
                                {event.teacher_names && event.teacher_names.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2" data-testid={`admin-event-teachers-${event.id}`}>
                                    <span className="text-sm text-gray-600">Insegnanti: </span>
                                    {event.teacher_names.map((teacherName, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {teacherName}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                {/* Locandina preview */}
                                {event.poster_url && (
                                  <div className="mt-2">
                                    {event.poster_url.toLowerCase().endsWith('.pdf') ? (
                                      <a
                                        href={`${BACKEND_URL}${event.poster_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 underline"
                                        data-testid={`view-poster-pdf-${event.id}`}
                                      >
                                        <FileText className="w-4 h-4 mr-1" />
                                        Visualizza Locandina (PDF)
                                      </a>
                                    ) : (
                                      <a
                                        href={`${BACKEND_URL}${event.poster_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`view-poster-img-${event.id}`}
                                      >
                                        <img
                                          src={`${BACKEND_URL}${event.poster_url}`}
                                          alt={`Locandina ${event.title}`}
                                          className="max-w-xs max-h-32 rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                          }}
                                        />
                                        <span className="text-sm text-red-500 hidden">Errore caricamento locandina</span>
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  onClick={() => setSelectedEventEnrollments({id: event.id, title: event.title})}
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-200 hover:bg-green-50"
                                  data-testid={`view-enrollments-button-${event.id}`}
                                >
                                  <Users className="w-4 h-4 mr-1" />
                                  Iscritti ({event.enrolled_count})
                                </Button>
                                <Button
                                  onClick={async () => {
                                    await fetchStudyMaterials(event.id);
                                    setSelectedEventForMaterials(event);
                                  }}
                                  size="sm"
                                  variant="outline"
                                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                  data-testid={`study-materials-button-${event.id}`}
                                >
                                  <BookOpen className="w-4 h-4 mr-1" />
                                  Materiale Didattico
                                </Button>
                                <Button
                                  onClick={() => startEditingEvent(event)}
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  data-testid={`edit-event-button-${event.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Modifica
                                </Button>
                                <Button
                                  onClick={() => generateEventCertificates(event.id, event.title)}
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                  disabled={generatingCertificates === event.id || event.enrolled_count === 0}
                                  data-testid={`generate-certificates-button-${event.id}`}
                                >
                                  {generatingCertificates === event.id ? (
                                    <>
                                      <span className="animate-spin mr-1">⏳</span>
                                      Generazione...
                                    </>
                                  ) : (
                                    <>
                                      <Award className="w-4 h-4 mr-1" />
                                      Genera Attestati
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => regenerateMissingCertificates(event.id, event.title)}
                                  size="sm"
                                  variant="outline"
                                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                  disabled={regeneratingCertificates === event.id}
                                  data-testid={`regenerate-certificates-button-${event.id}`}
                                  title="Rigenera i PDF mancanti per i certificati già emessi"
                                >
                                  {regeneratingCertificates === event.id ? (
                                    <>
                                      <span className="animate-spin mr-1">⏳</span>
                                      Rigenerazione...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-1" />
                                      Rigenera PDF
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => toggleEventEnrollment(event.id, event.title, event.enrollment_closed)}
                                  size="sm"
                                  variant="outline"
                                  className={event.enrollment_closed
                                    ? "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                    : "text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                                  }
                                  data-testid={`toggle-enrollment-button-${event.id}`}
                                >
                                  {event.enrollment_closed ? (
                                    <>
                                      <LockOpen className="w-4 h-4 mr-1" />
                                      Riapri Iscrizioni
                                    </>
                                  ) : (
                                    <>
                                      <Lock className="w-4 h-4 mr-1" />
                                      Chiudi Iscrizioni
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => deleteEvent(event.id, event.title)}
                                  size="sm"
                                  variant="destructive"
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                  data-testid={`delete-event-button-${event.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Elimina
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="enrollments" data-testid="admin-enrollments-content">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Users className="w-5 h-5" />
                      <span>Gestione Iscrizioni</span>
                      {enrollmentsCount.pending > 0 && (
                        <span className="bg-red-500 text-white text-sm px-2 py-1 rounded-full">
                          {enrollmentsCount.pending} in attesa
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Statistics */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-yellow-100 p-4 rounded-lg">
                          <h3 className="font-semibold text-yellow-800">In Attesa</h3>
                          <p className="text-2xl font-bold text-yellow-900">{enrollmentsCount.pending}</p>
                        </div>
                        <div className="bg-orange-100 p-4 rounded-lg">
                          <h3 className="font-semibold text-orange-800">Attesa Pagamento</h3>
                          <p className="text-2xl font-bold text-orange-900">{enrollmentsCount.pending_payment || 0}</p>
                        </div>
                        <div className="bg-green-100 p-4 rounded-lg">
                          <h3 className="font-semibold text-green-800">Approvate</h3>
                          <p className="text-2xl font-bold text-green-900">{enrollmentsCount.approved}</p>
                        </div>
                        <div className="bg-red-100 p-4 rounded-lg">
                          <h3 className="font-semibold text-red-800">Rifiutate</h3>
                          <p className="text-2xl font-bold text-red-900">{enrollmentsCount.rejected}</p>
                        </div>
                      </div>

                      {/* Pending Enrollments */}
                      {pendingEnrollments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Nessuna iscrizione in attesa di approvazione</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg">Iscrizioni in Attesa di Approvazione</h3>
                          {pendingEnrollments.map((enrollment) => (
                            <div key={enrollment.id} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-4">
                                    <div>
                                      <h4 className="font-semibold">{enrollment.user_name}
                                        {enrollment.athlete_class && (
                                          <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800">Classe {enrollment.athlete_class}</span>
                                        )}
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        Evento: <span className="font-medium">{enrollment.event_title}</span>
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Richiesta il: {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                      {enrollment.selected_dates && enrollment.selected_dates.length > 0 && (
                                        <div className="mt-1" data-testid={`selected-dates-${enrollment.id}`}>
                                          <p className="text-xs font-semibold text-blue-700">
                                            Date selezionate: {enrollment.selected_dates.length}
                                          </p>
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {enrollment.selected_dates.map(d => (
                                              <span key={d} className="inline-block px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 font-medium">
                                                {new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {enrollment.dance_analysis_requested && (
                                        <div className="mt-1" data-testid={`dance-analysis-info-${enrollment.id}`}>
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700 font-medium">
                                            Dance Analysis: {enrollment.dance_analysis_discipline}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <Button
                                    onClick={() => approveEnrollment(enrollment.id)}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`approve-enrollment-${enrollment.id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Approva
                                  </Button>
                                  <Button
                                    onClick={() => rejectEnrollment(enrollment.id)}
                                    size="sm"
                                    variant="destructive"
                                    data-testid={`reject-enrollment-${enrollment.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Rifiuta
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Pending Payment Enrollments */}
                      {pendingPaymentEnrollments.length > 0 && (
                        <div className="space-y-4 mt-8 pt-6 border-t">
                          <h3 className="font-semibold text-lg text-orange-700">
                            📧 Iscrizioni in Attesa di Pagamento ({pendingPaymentEnrollments.length})
                          </h3>
                          {pendingPaymentEnrollments.map((enrollment) => (
                            <div key={enrollment.id} className={`border rounded-lg p-4 ${enrollment.payment_type === 'mixed' ? 'border-blue-200 bg-blue-50' : 'border-orange-200 bg-orange-50'}`}>
                              <div className="flex items-center justify-between">
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-4">
                                    <div>
                                      <h4 className="font-semibold">{enrollment.user_name}</h4>
                                      <p className="text-sm text-gray-600">
                                        Evento: <span className="font-medium">{enrollment.event_title}</span>
                                      </p>
                                      {enrollment.payment_type === 'mixed' ? (
                                        <div className="space-y-1">
                                          <p className="text-sm text-blue-700 font-medium" data-testid={`mixed-payment-info-${enrollment.id}`}>
                                            Pagamento Misto - Totale: € {enrollment.payment_amount?.toFixed(2).replace('.', ',')}
                                          </p>
                                          <p className="text-xs text-red-600">
                                            Quota CIBS: € {enrollment.payment_amount_cibs?.toFixed(2).replace('.', ',')}
                                          </p>
                                          <p className="text-xs text-blue-600">
                                            Quota MIDAS: € {enrollment.payment_amount_midas?.toFixed(2).replace('.', ',')}
                                          </p>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-orange-700 font-medium">
                                          Importo richiesto: € {enrollment.payment_amount?.toFixed(2).replace('.', ',')}
                                        </p>
                                      )}
                                      <p className="text-xs text-gray-500">
                                        Email inviata a: {enrollment.user_email}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Richiesta il: {enrollment.payment_requested_at ? new Date(enrollment.payment_requested_at).toLocaleDateString('it-IT', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        }) : 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    onClick={() => sendPaymentReminder(enrollment.id)}
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    disabled={sendingPaymentReminder[enrollment.id]}
                                    data-testid={`send-payment-reminder-${enrollment.id}`}
                                  >
                                    <Mail className="w-4 h-4 mr-1" />
                                    {sendingPaymentReminder[enrollment.id] ? 'Invio...' : 'Sollecito'}
                                  </Button>
                                  <Button
                                    onClick={() => confirmPaymentReceived(enrollment.id)}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`confirm-payment-${enrollment.id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Conferma Pagamento
                                  </Button>
                                  <Button
                                    onClick={() => approveEnrollmentUnpaid(enrollment.id)}
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                                    data-testid={`approve-unpaid-${enrollment.id}`}
                                  >
                                    <Users className="w-4 h-4 mr-1" />
                                    Inserisci Non Pagato
                                  </Button>
                                  <Button
                                    onClick={() => rejectEnrollment(enrollment.id)}
                                    size="sm"
                                    variant="destructive"
                                    data-testid={`reject-payment-${enrollment.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Rifiuta
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Rejected Enrollments */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2 text-red-700">
                      <Trash2 className="w-5 h-5" />
                      <span>Iscrizioni Rifiutate</span>
                      {rejectedEnrollments.length > 0 && (
                        <Badge variant="destructive">{rejectedEnrollments.length}</Badge>
                      )}
                    </CardTitle>
                    <CardDescription>Iscrizioni rifiutate - puoi cancellarle per permettere all'atleta di iscriversi di nuovo</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {rejectedEnrollments.length === 0 ? (
                      <p className="text-gray-500 text-center py-6">Nessuna iscrizione rifiutata</p>
                    ) : (
                      <div className="space-y-3" data-testid="rejected-enrollments-list">
                        {rejectedEnrollments.map((enrollment) => (
                          <div key={enrollment.id} className="border border-red-100 rounded-lg p-4 bg-red-50/50 flex items-center justify-between" data-testid={`rejected-enrollment-${enrollment.id}`}>
                            <div>
                              <h4 className="font-semibold text-red-900">{enrollment.user_name}</h4>
                              <p className="text-sm text-red-700">
                                Evento: <span className="font-medium">{enrollment.event_title}</span>
                              </p>
                              <p className="text-xs text-gray-500">
                                Richiesta il: {new Date(enrollment.enrolled_at).toLocaleDateString('it-IT', {
                                  day: '2-digit', month: '2-digit', year: 'numeric'
                                })}
                              </p>
                            </div>
                            <Button
                              onClick={() => deleteRejectedEnrollment(enrollment.id, enrollment.user_name, enrollment.event_title)}
                              size="sm"
                              variant="destructive"
                              className="bg-red-600 hover:bg-red-700"
                              data-testid={`delete-rejected-${enrollment.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancella
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="analysis" data-testid="admin-analysis-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BookOpen className="w-5 h-5" />
                    <span>Tutte le Match Analysis</span>
                  </CardTitle>
                  <CardDescription>
                    Panoramica di tutte le match analysis create dai teacher
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filtri di ricerca per admin match analysis */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">Filtri di Ricerca</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="filter-admin-date">Data</Label>
                        <Input
                          id="filter-admin-date"
                          type="date"
                          value={analysisFilters.date}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, date: e.target.value}))}
                          placeholder="Filtra per data"
                        />
                      </div>
                      <div>
                        <Label htmlFor="filter-admin-event">Evento</Label>
                        <Input
                          id="filter-admin-event"
                          value={analysisFilters.event}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, event: e.target.value}))}
                          placeholder="Cerca per evento"
                        />
                      </div>
                      <div>
                        <Label htmlFor="filter-admin-athlete">Atleta</Label>
                        <Input
                          id="filter-admin-athlete"
                          value={analysisFilters.athlete}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, athlete: e.target.value}))}
                          placeholder="Cerca per atleta"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={clearAnalysisFilters}
                          className="w-full"
                        >
                          Pulisci Filtri
                        </Button>
                      </div>
                    </div>
                  </div>

                  {filterMatchAnalyses(allMatchAnalyses).length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-admin-analysis">
                      Nessuna match analysis presente nel sistema
                    </p>
                  ) : (
                    <div className="space-y-4" data-testid="admin-analysis-list">
                      {filterMatchAnalyses(allMatchAnalyses).map((analysis) => (
                        <div 
                          key={analysis.id} 
                          className="border rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer" 
                          data-testid={`admin-analysis-${analysis.id}`}
                          onClick={() => setSelectedAnalysis(analysis)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-blue-700" data-testid={`admin-analysis-event-${analysis.id}`}>{analysis.event_title}</h3>
                            <span className="text-sm text-gray-500" data-testid={`admin-analysis-date-${analysis.id}`}>
                              {formatDate(analysis.created_at)}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                            <p className="text-sm text-gray-600" data-testid={`admin-analysis-athlete-${analysis.id}`}>
                              Atleta: {analysis.athlete_name}
                            </p>
                            <p className="text-sm text-gray-600" data-testid={`admin-analysis-teacher-${analysis.id}`}>
                              Teacher: {analysis.teacher_name}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded p-3 mb-3" data-testid={`admin-analysis-content-${analysis.id}`}>
                            <p className="text-gray-700 line-clamp-3">{analysis.content}</p>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-1" />
                              Visualizza Dettagli
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMatchAnalysis(analysis.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Elimina
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="detailed" data-testid="admin-detailed-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BookOpen className="w-5 h-5" />
                    <span>Tutte le Match Analysis Dettagliate</span>
                  </CardTitle>
                  <CardDescription>
                    Panoramica di tutte le analisi dettagliate create dai teacher
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filtri di ricerca per detailed analysis admin */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">Filtri di Ricerca</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="filter-admin-detailed-date">Data</Label>
                        <Input
                          id="filter-admin-detailed-date"
                          type="date"
                          value={analysisFilters.date}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, date: e.target.value}))}
                          placeholder="Filtra per data"
                        />
                      </div>
                      <div>
                        <Label htmlFor="filter-admin-detailed-event">Evento</Label>
                        <Input
                          id="filter-admin-detailed-event"
                          value={analysisFilters.event}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, event: e.target.value}))}
                          placeholder="Cerca per evento"
                        />
                      </div>
                      <div>
                        <Label htmlFor="filter-admin-detailed-athlete">Atleta</Label>
                        <Input
                          id="filter-admin-detailed-athlete"
                          value={analysisFilters.athlete}
                          onChange={(e) => setAnalysisFilters(prev => ({...prev, athlete: e.target.value}))}
                          placeholder="Cerca per atleta"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={clearAnalysisFilters}
                          className="w-full"
                        >
                          Pulisci Filtri
                        </Button>
                      </div>
                    </div>
                  </div>

                  {filterMatchAnalyses(allDetailedMatchAnalyses).length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-admin-detailed-analysis">
                      Nessuna analisi dettagliata presente nel sistema
                    </p>
                  ) : (
                    <div className="space-y-4" data-testid="admin-detailed-analysis-list">
                      {filterMatchAnalyses(allDetailedMatchAnalyses).map((analysis) => (
                        <div 
                          key={analysis.id} 
                          className="border rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer" 
                          data-testid={`admin-detailed-analysis-${analysis.id}`}
                          onClick={() => setSelectedDetailedAnalysis(analysis)}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-semibold text-lg" data-testid={`admin-detailed-analysis-event-${analysis.id}`}>
                                {analysis.event_title}
                              </h3>
                              <div className="space-y-1 text-sm text-gray-600">
                                <p data-testid={`admin-detailed-analysis-athlete-${analysis.id}`}>
                                  Atleta: {analysis.athlete_name}
                                </p>
                                <p data-testid={`admin-detailed-analysis-teacher-${analysis.id}`}>
                                  Insegnante: {analysis.teacher_name}
                                </p>
                                <span className="text-gray-500" data-testid={`admin-detailed-analysis-date-${analysis.id}`}>
                                  {formatDate(analysis.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-purple-600" data-testid={`admin-detailed-analysis-total-${analysis.id}`}>
                                {analysis.total_score}/10
                              </div>
                              <div className="text-sm text-gray-500">Media Punteggi</div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                            {Object.entries(analysis.scores || {}).map(([key, value]) => (
                              <div key={key} className="text-center p-2 bg-gray-50 rounded" data-testid={`admin-detailed-score-display-${key}-${analysis.id}`}>
                                <div className="text-xs text-gray-600 mb-1 font-medium">
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </div>
                                <div className="font-semibold text-purple-600">{value}/10</div>
                              </div>
                            ))}
                          </div>
                          
                          {analysis.notes && (
                            <div className="bg-purple-50 rounded p-3 border border-purple-200 mb-3" data-testid={`admin-detailed-analysis-notes-${analysis.id}`}>
                              <h4 className="font-medium text-purple-800 mb-1">Note:</h4>
                              <p className="text-purple-700 text-sm line-clamp-2">{analysis.notes}</p>
                            </div>
                          )}
                          
                          <div className="flex justify-end space-x-2">
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-1" />
                              Visualizza Dettagli
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteDetailedMatchAnalysis(analysis.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Elimina
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            

            {/* Match Analysis Results Tab */}
            <TabsContent value="match-results" data-testid="admin-match-results-content">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Plus className="w-5 h-5" />
                      <span>Risultati MATCH ANALYSIS</span>
                    </CardTitle>
                    <CardDescription>Inserisci i risultati della Match Analysis per un atleta</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>Evento *</Label>
                        <select
                          value={matchResultForm?.event_id || ''}
                          onChange={(e) => setMatchResultForm({...matchResultForm, event_id: e.target.value})}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          data-testid="match-result-event-select"
                        >
                          <option value="">Seleziona evento...</option>
                          {events.map(ev => (
                            <option key={ev.id} value={ev.id}>{ev.title}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Atleta *</Label>
                        <div className="relative" data-testid="match-result-athlete-search">
                          <Input
                            type="text"
                            value={matchResultForm?.athlete_search || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setMatchResultForm({...matchResultForm, athlete_search: val, athlete_id: val ? matchResultForm.athlete_id : ''});
                            }}
                            placeholder="Cerca per nome o cognome..."
                            className={matchResultForm?.athlete_id ? 'border-green-400 bg-green-50' : ''}
                            data-testid="match-result-athlete-input"
                          />
                          {matchResultForm?.athlete_search && !matchResultForm?.athlete_id && (() => {
                            const query = matchResultForm.athlete_search.toLowerCase();
                            const results = users.filter(u =>
                              (u.roles || []).includes('atleta') && u.approved &&
                              (`${u.name} ${u.surname}`.toLowerCase().includes(query) ||
                               u.surname.toLowerCase().includes(query) ||
                               u.name.toLowerCase().includes(query))
                            ).slice(0, 8);
                            return results.length > 0 ? (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                {results.map(u => (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => setMatchResultForm({...matchResultForm, athlete_id: u.id, athlete_search: `${u.name} ${u.surname}`})}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-700 transition-colors border-b border-gray-100 last:border-0"
                                    data-testid={`athlete-option-${u.id}`}
                                  >
                                    <span className="font-medium">{u.name} {u.surname}</span>
                                    <span className="text-gray-400 ml-2 text-xs">{u.email}</span>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-500">
                                Nessun atleta trovato
                              </div>
                            );
                          })()}
                          {matchResultForm?.athlete_id && (
                            <button
                              type="button"
                              onClick={() => setMatchResultForm({...matchResultForm, athlete_id: '', athlete_search: ''})}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-xs"
                              data-testid="clear-athlete-btn"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4 mb-4">
                      <div>
                        <Label>Descrizione del Risultato *</Label>
                        <textarea
                          value={matchResultForm?.content ?? 'Vedi Certificato allegato.'}
                          onChange={(e) => setMatchResultForm({...matchResultForm, content: e.target.value})}
                          placeholder="Vedi Certificato allegato."
                          rows={3}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          data-testid="match-result-content"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Totale Voti Ottenuti <span className="text-gray-500 text-xs font-normal">da convertire in crediti secondo il regolamento CIBS</span> *</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={matchResultForm?.result_score ?? ''}
                            onChange={(e) => setMatchResultForm({...matchResultForm, result_score: e.target.value})}
                            placeholder="Es: 8.5"
                            data-testid="match-result-score"
                          />
                        </div>
                        <div>
                          <Label>Giudizi e Consigli <span className="text-gray-400 text-xs">(facoltativo)</span></Label>
                          <textarea
                            value={matchResultForm?.judgments_advice ?? 'Vedi Certificato allegato.'}
                            onChange={(e) => setMatchResultForm({...matchResultForm, judgments_advice: e.target.value})}
                            placeholder="Vedi Certificato allegato."
                            rows={2}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            data-testid="match-result-advice"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Certificato / Attestato <span className="text-gray-400 text-xs">(PDF, facoltativo)</span></Label>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (!file) {
                              setMatchResultForm({...matchResultForm, certificate_pdf_data: null, certificate_pdf_filename: null});
                              return;
                            }
                            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                              toast.error('Carica un file PDF');
                              e.target.value = '';
                              return;
                            }
                            const MAX_BYTES = 15 * 1024 * 1024; // 15MB
                            if (file.size > MAX_BYTES) {
                              toast.error('Il file supera i 15MB. Carica un PDF più piccolo.');
                              e.target.value = '';
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = () => {
                              const result = reader.result || '';
                              const base64 = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : result;
                              setMatchResultForm({
                                ...matchResultForm,
                                certificate_pdf_data: base64,
                                certificate_pdf_filename: file.name
                              });
                              toast.success('Certificato caricato. Verrà salvato all\'invio.');
                            };
                            reader.onerror = () => toast.error('Errore lettura file PDF');
                            reader.readAsDataURL(file);
                          }}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          data-testid="match-result-certificate-input"
                        />
                        {matchResultForm?.certificate_pdf_filename && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                            <span data-testid="match-result-certificate-name">{matchResultForm.certificate_pdf_filename}</span>
                            <button
                              type="button"
                              onClick={() => setMatchResultForm({...matchResultForm, certificate_pdf_data: null, certificate_pdf_filename: null})}
                              className="text-red-500 hover:underline ml-auto"
                              data-testid="match-result-certificate-remove"
                            >
                              Rimuovi
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={submitMatchResult}
                      disabled={!matchResultForm?.event_id || !matchResultForm?.athlete_id || !matchResultForm?.result_score}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="submit-match-result-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Inserisci Risultato
                    </Button>
                  </CardContent>
                </Card>

                {/* Existing Match Analyses */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <ClipboardList className="w-5 h-5" />
                      <span>Risultati Inseriti</span>
                    </CardTitle>
                    <CardDescription>
                      Ordinati dal più recente. Clicca un record per vederne il dettaglio.
                    </CardDescription>
                    <div className="relative mt-3">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        value={matchResultsSearch}
                        onChange={(e) => setMatchResultsSearch(e.target.value)}
                        placeholder="Cerca atleta per nome o cognome..."
                        className="pl-9"
                        data-testid="admin-match-results-search-input"
                      />
                      {matchResultsSearch && (
                        <button
                          type="button"
                          onClick={() => setMatchResultsSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                          data-testid="admin-match-results-search-clear"
                          aria-label="Pulisci ricerca"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {matchResultsSearch && (
                      <p className="text-xs text-gray-500 mt-1" data-testid="admin-match-results-search-count">
                        {sortedFilteredMatchAnalyses.length} risultat{sortedFilteredMatchAnalyses.length === 1 ? 'o' : 'i'} su {adminMatchAnalyses.length}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {adminMatchAnalyses.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Nessun risultato inserito</p>
                    ) : sortedFilteredMatchAnalyses.length === 0 ? (
                      <p className="text-gray-500 text-center py-8" data-testid="admin-match-results-no-results">
                        Nessun atleta corrisponde alla ricerca
                      </p>
                    ) : (
                      <div className="space-y-2" data-testid="admin-match-results-list">
                        {sortedFilteredMatchAnalyses.map((analysis) => {
                          const isExpanded = expandedMatchResultId === analysis.id;
                          const formattedDate = analysis.created_at
                            ? new Date(analysis.created_at).toLocaleDateString('it-IT')
                            : '';
                          return (
                            <div
                              key={analysis.id}
                              className="border rounded-lg hover:bg-gray-50 transition-colors"
                              data-testid={`admin-match-result-${analysis.id}`}
                            >
                              <button
                                type="button"
                                onClick={() => setExpandedMatchResultId(isExpanded ? null : analysis.id)}
                                className="w-full text-left px-3 py-2 flex items-center justify-between gap-2"
                                data-testid={`admin-match-result-toggle-${analysis.id}`}
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-gray-900 truncate">
                                    {analysis.event_title}{formattedDate ? ` - ${formattedDate}` : ''}
                                  </p>
                                  <p className="text-sm text-gray-600 truncate">
                                    Atleta: <span className="font-medium">{analysis.athlete_name}</span>
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {analysis.result_score != null && (
                                    <Badge className="bg-purple-100 text-purple-800 font-bold">{analysis.result_score}</Badge>
                                  )}
                                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </button>
                              {isExpanded && (
                                <div className="px-3 pb-3 pt-1 border-t border-gray-100 bg-gray-50/50" data-testid={`admin-match-result-details-${analysis.id}`}>
                                  {analysis.content && (
                                    <div className="mt-2">
                                      <p className="text-xs font-semibold text-gray-500 mb-0.5">Descrizione</p>
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{analysis.content}</p>
                                    </div>
                                  )}
                                  {analysis.judgments_advice && (
                                    <div className="bg-blue-50 rounded p-2 mt-2 border border-blue-100">
                                      <p className="text-xs font-semibold text-blue-600 mb-0.5">Giudizi e Consigli</p>
                                      <p className="text-sm text-blue-800 whitespace-pre-wrap">{analysis.judgments_advice}</p>
                                    </div>
                                  )}
                                  {analysis.has_certificate && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        onClick={async (ev) => {
                                          ev.stopPropagation();
                                          try {
                                            const resp = await axios.get(
                                              `${API}/match-analysis/${analysis.id}/certificate`,
                                              { responseType: 'blob' }
                                            );
                                            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.target = '_blank';
                                            a.rel = 'noopener noreferrer';
                                            a.download = analysis.certificate_pdf_filename || `certificato_${analysis.id}.pdf`;
                                            document.body.appendChild(a);
                                            a.click();
                                            a.remove();
                                            setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                                          } catch (err) {
                                            console.error('Errore download certificato', err);
                                          }
                                        }}
                                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                        data-testid={`admin-match-certificate-${analysis.id}`}
                                      >
                                        📎 {analysis.certificate_pdf_filename || 'Scarica Certificato'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="ranking" data-testid="admin-ranking-content">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <Award className="w-5 h-5" />
                          <span>Ranking Atleti</span>
                        </CardTitle>
                        <CardDescription>
                          Tutti gli atleti ordinati per <strong>Voto Finale</strong> (Crediti Formativi + Crediti Tecnici).
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          value={rankingAcademicYear}
                          onChange={(e) => handleRankingYearChange(e.target.value)}
                          disabled={rankingLoading}
                          className="rounded-md border border-purple-300 bg-white px-3 py-2 text-sm font-medium text-purple-700 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:opacity-50"
                          data-testid="ranking-academic-year-select"
                        >
                          <option value="all">Tutte le stagioni</option>
                          {rankingAvailableYears.map((y) => (
                            <option key={y} value={y}>Anno {y}</option>
                          ))}
                        </select>
                        <Button
                          variant="outline"
                          onClick={() => exportRanking('pdf')}
                          disabled={rankingExporting !== null || rankingData.length === 0}
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          data-testid="ranking-export-pdf-btn"
                        >
                          {rankingExporting === 'pdf' ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4 mr-1" />
                          )}
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => exportRanking('excel')}
                          disabled={rankingExporting !== null || rankingData.length === 0}
                          className="border-green-300 text-green-700 hover:bg-green-50"
                          data-testid="ranking-export-excel-btn"
                        >
                          {rankingExporting === 'excel' ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          Excel
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchRanking()}
                          disabled={rankingLoading}
                          data-testid="ranking-refresh-btn"
                          title="Aggiorna"
                        >
                          <RefreshCw className={`w-4 h-4 ${rankingLoading ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>
                    <div className="relative mt-3">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        value={rankingSearch}
                        onChange={(e) => setRankingSearch(e.target.value)}
                        placeholder="Cerca atleta per nome, cognome o email..."
                        className="pl-9"
                        data-testid="ranking-search-input"
                      />
                      {rankingSearch && (
                        <button
                          type="button"
                          onClick={() => setRankingSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                          data-testid="ranking-search-clear"
                          aria-label="Pulisci ricerca"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {rankingLoading ? (
                      <div className="flex items-center justify-center py-12 text-gray-500">
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Caricamento ranking...
                      </div>
                    ) : rankingData.length === 0 ? (
                      <p className="text-gray-500 text-center py-8" data-testid="ranking-empty">
                        Nessun atleta trovato
                      </p>
                    ) : filteredRanking.length === 0 ? (
                      <p className="text-gray-500 text-center py-8" data-testid="ranking-no-search-results">
                        Nessun atleta corrisponde alla ricerca
                      </p>
                    ) : (
                      <div className="overflow-x-auto" data-testid="ranking-table-wrapper">
                        <table className="w-full text-sm" data-testid="ranking-table">
                          <thead>
                            <tr className="bg-purple-50 text-purple-900">
                              <th className="text-center px-3 py-2 font-semibold w-12">Pos.</th>
                              <th className="text-left px-3 py-2 font-semibold">Atleta</th>
                              <th className="text-left px-3 py-2 font-semibold hidden md:table-cell">Email</th>
                              <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Classe</th>
                              <th className="text-left px-3 py-2 font-semibold hidden lg:table-cell">Regione</th>
                              <th className="text-center px-3 py-2 font-semibold hidden sm:table-cell" title="Crediti Formativi">C. Form.</th>
                              <th className="text-center px-3 py-2 font-semibold hidden sm:table-cell" title="Crediti Tecnici">C. Tecn.</th>
                              <th className="text-center px-3 py-2 font-semibold" title="Voto Finale = Formativi + Tecnici">Voto Finale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRanking.map((row) => {
                              const podium = row.rank === 1 ? 'bg-yellow-50' : row.rank === 2 ? 'bg-gray-100' : row.rank === 3 ? 'bg-orange-50' : '';
                              return (
                                <tr
                                  key={row.athlete_id}
                                  className={`border-b border-gray-100 hover:bg-purple-50/40 ${podium}`}
                                  data-testid={`ranking-row-${row.athlete_id}`}
                                >
                                  <td className="text-center px-3 py-2 font-bold">
                                    {row.rank === 1 && <span title="1° posto">🥇</span>}
                                    {row.rank === 2 && <span title="2° posto">🥈</span>}
                                    {row.rank === 3 && <span title="3° posto">🥉</span>}
                                    {row.rank > 3 && <span className="text-gray-700">{row.rank}</span>}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="font-medium text-gray-900">
                                      {row.surname} {row.name}
                                    </div>
                                    <div className="text-xs text-gray-500 md:hidden">{row.email}</div>
                                    <div className="text-xs text-gray-500 sm:hidden">
                                      Form: {row.formative_credits} · Tecn: {row.technical_credits}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 hidden md:table-cell text-gray-600">{row.email}</td>
                                  <td className="px-3 py-2 hidden lg:table-cell text-gray-600">{row.athlete_class || '-'}</td>
                                  <td className="px-3 py-2 hidden lg:table-cell text-gray-600">{row.region || '-'}</td>
                                  <td className="text-center px-3 py-2 hidden sm:table-cell text-blue-700 font-medium" data-testid={`ranking-formative-${row.athlete_id}`}>
                                    {row.formative_credits}
                                  </td>
                                  <td className="text-center px-3 py-2 hidden sm:table-cell text-emerald-700 font-medium" data-testid={`ranking-technical-${row.athlete_id}`}>
                                    {row.technical_credits}
                                  </td>
                                  <td className="text-center px-3 py-2" data-testid={`ranking-total-${row.athlete_id}`}>
                                    <Badge className="bg-purple-100 text-purple-800 font-bold text-base px-3 py-1">
                                      {row.total_credits}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="text-xs text-gray-500 mt-3" data-testid="ranking-summary">
                          {rankingSearch
                            ? `${filteredRanking.length} risultat${filteredRanking.length === 1 ? 'o' : 'i'} su ${rankingData.length} atleti`
                            : `Totale: ${rankingData.length} atleti`}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="certificates" data-testid="admin-certificates-content">
              <div className="space-y-6">
                {/* Create Certificate Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Plus className="w-5 h-5" />
                      <span>Crea Certificato</span>
                    </CardTitle>
                    <CardDescription>
                      Rilascia certificati ai discenti
                    </CardDescription>
                  </CardHeader>
                <CardContent>
                  <form onSubmit={createCertificateWithFile} className="space-y-4" data-testid="create-certificate-form">
                    <div className="space-y-2">
                      <Label htmlFor="user_id">DISCENTE *</Label>
                      <Select value={newCertificate.user_id} onValueChange={(value) => setNewCertificate({...newCertificate, user_id: value})} data-testid="certificate-user-select">
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona discente" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.approved).map((user) => (
                            <SelectItem key={user.id} value={user.id} data-testid={`cert-user-option-${user.id}`}>
                              {user.name} {user.surname} {user.roles ? `(${user.roles.join(', ')})` : user.role ? `(${user.role})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="title">Titolo Certificato *</Label>
                      <Input
                        id="title"
                        value={newCertificate.title}
                        onChange={(e) => setNewCertificate({...newCertificate, title: e.target.value})}
                        placeholder="Es. Certificato di Partecipazione Stage Danza Classica"
                        required
                        data-testid="certificate-title-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Descrizione</Label>
                      <Textarea
                        id="description"
                        value={newCertificate.description}
                        onChange={(e) => setNewCertificate({...newCertificate, description: e.target.value})}
                        placeholder="Descrizione del certificato (opzionale)"
                        data-testid="certificate-description-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="pdf_file">File PDF Certificato</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          id="pdf_file"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setCertificateFile(e.target.files[0])}
                          data-testid="certificate-pdf-input"
                        />
                        <Upload className="w-4 h-4 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-500">
                        Carica un file PDF per il certificato (opzionale)
                      </p>
                    </div>
                    
                    <Button type="submit" className="w-full" data-testid="create-certificate-button">
                      Rilascia Certificato
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* All Certificates View */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Award className="w-5 h-5" />
                    <span>Tutti i Certificati</span>
                  </CardTitle>
                  <CardDescription>
                    Panoramica di tutti i certificati emessi
                  </CardDescription>
                  {(() => {
                    const missing = (allCertificates || []).filter((c) => c?.file_available === false);
                    if (missing.length === 0) return null;
                    return (
                      <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="admin-cert-missing-banner">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-amber-900" data-testid="admin-cert-missing-count">
                              {missing.length} attestat{missing.length === 1 ? 'o' : 'i'} con PDF non disponibile
                            </p>
                            <p className="text-xs text-amber-800">I PDF mancanti verranno rigenerati e salvati in modo stabile (MongoDB) per essere sempre scaricabili dagli atleti.</p>
                          </div>
                        </div>
                        <Button
                          onClick={regenerateAllMissingCertificates}
                          disabled={regeneratingCertificates === '__all__'}
                          className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                          data-testid="admin-cert-regenerate-missing-btn"
                        >
                          {regeneratingCertificates === '__all__' ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Rigenerazione in corso...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Ri-Genera gli attestati mancanti
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })()}
                </CardHeader>
                <CardContent>
                  {/* Filtri di ricerca per certificati */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-3">Filtri di Ricerca</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="filter-cert-event">Evento/Titolo</Label>
                        <Input
                          id="filter-cert-event"
                          value={certificateFilters.event}
                          onChange={(e) => setCertificateFilters(prev => ({...prev, event: e.target.value}))}
                          placeholder="Cerca per evento o titolo"
                        />
                      </div>
                      <div>
                        <Label htmlFor="filter-cert-athlete">Discente</Label>
                        <Input
                          id="filter-cert-athlete"
                          value={certificateFilters.athlete}
                          onChange={(e) => setCertificateFilters(prev => ({...prev, athlete: e.target.value}))}
                          placeholder="Cerca per discente"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={clearCertificateFilters}
                          className="w-full"
                        >
                          Pulisci Filtri
                        </Button>
                      </div>
                    </div>
                  </div>

                  {filterCertificates(allCertificates).length === 0 ? (
                    <p className="text-gray-500 text-center py-8" data-testid="no-admin-certificates">
                      Nessun certificato presente nel sistema
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-certificates-list">
                      {filterCertificates(allCertificates).map((certificate) => (
                        <div key={certificate.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`admin-certificate-${certificate.id}`}>
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg text-blue-700" data-testid={`admin-certificate-title-${certificate.id}`}>
                                {certificate.title}
                              </h3>
                              <p className="text-sm text-gray-600 mb-1" data-testid={`admin-certificate-athlete-${certificate.id}`}>
                                Discente: {certificate.user_name}
                              </p>
                              <span className="text-xs text-gray-500" data-testid={`admin-certificate-date-${certificate.id}`}>
                                {formatDate(certificate.issued_at)}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-2">
                              {certificate.pdf_filename && certificate.file_available !== false ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const response = await axios.get(`${API}/certificates/${certificate.id}/download`, {
                                        responseType: 'blob',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', `${certificate.title.replace(/ /g, '_')}.pdf`);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      toast.error('Errore nel download del certificato. Il file potrebbe non essere disponibile.');
                                    }
                                  }}
                                  data-testid={`download-cert-admin-${certificate.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  PDF
                                </Button>
                              ) : certificate.pdf_filename && certificate.file_available === false ? (
                                <div className="flex flex-col space-y-1">
                                  <span className="text-xs text-orange-600">⚠️ PDF non disponibile</span>
                                  <label className="cursor-pointer">
                                    <input
                                      type="file"
                                      accept=".pdf"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          regenerateCertificate(certificate.id, e.target.files[0]);
                                        }
                                      }}
                                    />
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors">
                                      <Upload className="w-3 h-3 mr-1" />
                                      Rigenera PDF
                                    </span>
                                  </label>
                                </div>
                              ) : null}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                                onClick={() => deleteCertificate(certificate.id)}
                                data-testid={`delete-cert-admin-${certificate.id}`}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Elimina
                              </Button>
                            </div>
                          </div>
                          
                          {certificate.description && (
                            <div className="bg-gray-50 rounded p-2 mb-2">
                              <p className="text-sm text-gray-700">{certificate.description}</p>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>ID: {certificate.id.substring(0, 8)}</span>
                            {certificate.pdf_filename ? (
                              <span className="text-green-600 font-medium">✓ PDF allegato</span>
                            ) : (
                              <span className="text-amber-600 font-medium">⚠ Solo testo</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>
            </TabsContent>

            <TabsContent value="reports" data-testid="admin-reports-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>Report e Statistiche</span>
                  </CardTitle>
                  <CardDescription>
                    Panoramica generale del sistema
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-600 text-sm font-medium">Utenti Totali</p>
                          <p className="text-2xl font-bold text-blue-800">{users.length}</p>
                        </div>
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-600 text-sm font-medium">Eventi Attivi</p>
                          <p className="text-2xl font-bold text-green-800">{events.length}</p>
                        </div>
                        <Calendar className="w-8 h-8 text-green-600" />
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-600 text-sm font-medium">Match Analysis</p>
                          <p className="text-2xl font-bold text-purple-800">{allMatchAnalyses.length + allDetailedMatchAnalyses.length}</p>
                        </div>
                        <BookOpen className="w-8 h-8 text-purple-600" />
                      </div>
                    </div>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-amber-600 text-sm font-medium">Certificati</p>
                          <p className="text-2xl font-bold text-amber-800">{allCertificates.length}</p>
                        </div>
                        <Award className="w-8 h-8 text-amber-600" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" data-testid="admin-analytics-content">
              <AdminAnalytics />
            </TabsContent>

            {/* Audit Log Tab */}
            <TabsContent value="audit" data-testid="admin-audit-content">
              <AdminAuditLog />
            </TabsContent>

            <TabsContent value="incomplete-profiles" data-testid="admin-incomplete-profiles-content">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Profili Atleta Incompleti
                    </CardTitle>
                    <CardDescription>
                      Atleti che non hanno ancora compilato tutti i dati obbligatori del profilo
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="destructive" className="text-sm px-3 py-1">
                      {incompleteProfiles.length} incompleti
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchIncompleteProfiles}
                      data-testid="refresh-incomplete-profiles-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Aggiorna
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {incompleteProfiles.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                      <p className="font-medium text-lg">Tutti i profili sono completi!</p>
                      <p className="text-sm mt-1">Nessun atleta ha campi obbligatori mancanti.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {incompleteProfiles.map((profile) => (
                        <div
                          key={profile.id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                          data-testid={`incomplete-profile-${profile.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">
                                  {profile.surname} {profile.name}
                                </h4>
                                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                  {profile.completed_count}/{profile.total_required} completati
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500 mb-2">{profile.email}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {profile.missing_fields.map((field) => (
                                  <span
                                    key={field.field}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                                  >
                                    {field.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="shrink-0 text-orange-600 border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                              onClick={() => sendProfileReminder(profile.id, `${profile.name} ${profile.surname}`)}
                              disabled={sendingReminder[profile.id]}
                              data-testid={`send-reminder-${profile.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              {sendingReminder[profile.id] ? 'Invio...' : 'Invia Sollecito'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
  );
};
